import './poster.scss'

import React, {
	RefObject,
	createRef,
	forwardRef,
	useEffect,
	useRef,
	useState
} from 'react'
import {format, isValid} from 'date-fns'

import {FunctionComponent} from '@codeurs/front'
import {Logo} from './logo'
import {RevolutionLogo} from './revolutionlogo'
import composeRefs from '@seznam/compose-react-refs'
import nldate from 'date-fns/esm/locale/nl'
import {style} from 'util/campagne/style'

type EventDetails = {
	location: string
	venue: string
	date_start: string
	date_end: string
	hour: string
}

type TagLineWords = {
	organisation: string
	sharing: string
}

const styles = style('poster')

const findClosest = (x, arr) => {
	const indexArr = arr.map((k) => Math.abs(k - x))
	const min = Math.min.apply(Math, indexArr)
	return arr[indexArr.indexOf(min)]
}

const SPLITTABLE_CHARS = new Set([' ', '-', '\n', '\t'])

const splitInTwo = (text: string) => {
	if (text.length <= 15) return [text]
	const splits = []
	for (let i = 0; i < text.length; i++)
		if (SPLITTABLE_CHARS.has(text[i])) splits.push(i + 1)
	if (splits.length < 1) return [text]
	const ideal = Math.round(text.length / 2)
	const closest = findClosest(ideal, splits)
	return [text.slice(0, closest), text.slice(closest)]
}

const Highlight: FunctionComponent<{
	transform?: string
	fill?: string
	paddingX?: number
	paddingY?: number
	source: RefObject<SVGTextElement>
}> = ({transform, fill = 'white', paddingX = 15, paddingY = 0, source}) => {
	const el = useRef<SVGRectElement>()
	useEffect(() => {
		const box = source.current.getBBox()
		el.current.setAttribute('width', String(box.width + 2.5 * paddingX))
		el.current.setAttribute('height', String(box.height + 2 * paddingY))
		el.current.setAttribute('x', String(box.x - paddingX))
		el.current.setAttribute('y', String(box.y - paddingY - 1))
	})
	return <rect transform={transform} fill={fill} ref={el} />
}

type HighlightedText = {
	transform?: string
	fill?: string
	text: string
}

const HighlightedText = forwardRef<SVGTextElement, HighlightedText>(
	({transform, fill, text, ...props}, ref) => {
		return (
			<text transform={transform} fill={fill} ref={ref} {...props}>
				<tspan>{text}</tspan>
			</text>
		)
	}
)

const LINE_HEIGHT = 60

const createTagline = (options: TagLineWords & {variant: Variant}) => {
	const {organisation, sharing, variant} = options

	const lines = {
		organisation: splitInTwo(organisation),
		sharing: splitInTwo(sharing)
	}
	let y = 0
	const x = (_) => _ * 1
	const bottom = variant === variants.landscape ? ' translate(550 -50)' : ''
	const green = '#6CC053'
	const grey = '#4C4C4C'
	return [
		{
			transform: `rotate(3) translate(${x(135)} ${y - 16})`,
			fill: green,
			text: 'Wij,'
		},
		((y += 5),
		lines.organisation.map((line, i) => ({
			transform: `rotate(359) translate(${x(26 + 6 * i)} ${(y +=
				LINE_HEIGHT + 5)})`,
			fill: grey,
			text: line
		}))),
		{
			transform: `rotate(3) translate(${x(90)} ${(y += LINE_HEIGHT + 5)})`,
			fill: green,
			text: 'steunen met'
		},
		((y += 25),
		lines.sharing.map((line, i) => ({
			transform: `${bottom} rotate(${1 + i * 2}) translate(${x(22)} ${(y +=
				LINE_HEIGHT + 2)}) `,
			fill: grey,
			text: line
		}))),
		{
			transform: `${bottom} rotate(5) translate(${x(
				50
			)} ${(y += LINE_HEIGHT)}) `,
			fill: green,
			text: 'de 25%-revolutie'
		}
	].flat()
}

const Tagline: FunctionComponent<TagLineWords & {variant: Variant}> = (
	props
) => {
	const group = useRef<SVGGElement>()
	const tagline = createTagline(props)
	const refs = useRefs(tagline.length)
	const {maxWidth, maxHeight, centerIn} = props.variant.tagline
	useEffect(() => {
		const {width, height} = group.current.getBBox()
		const ratio = Math.min(1, maxWidth / width, maxHeight / height)
		const offset = centerIn ? (centerIn - width * ratio) / 2 : 0
		group.current.setAttribute(
			'transform',
			`translate(${offset} 0) scale(${ratio})`
		)
	})
	return (
		<g transform="translate(0 75)">
			{/*<rect fill="red" width={centerIn} height={maxHeight} x="0" y="0" />*/}
			<g ref={group}>
				{tagline.map((line, i) => (
					<Highlight transform={line.transform} source={refs[i]} />
				))}
				{tagline.map((line, i) => (
					<HighlightedText
						font-size="58.32"
						letter-spacing="-0.014em"
						font-family="Campton"
						font-weight="bold"
						{...line}
						ref={refs[i]}
					/>
				))}
			</g>
		</g>
	)
}

const useRefs = (amount: number) => {
	const refs = useRef([])
	if (amount === refs.current.length) return refs.current
	return (refs.current = Array.from(Array(amount)).map(
		(_, i) => refs.current[i] || createRef()
	))
}

const Info: FunctionComponent<
	EventDetails & {centerIn?: number; scale: number}
> = ({location, venue, date_start, date_end, hour, centerIn, scale}) => {
	const infoParts = [
		{
			transform: 'matrix(0.999, -0.035, 0.035, 0.999, 13.98, 41.407)',
			text: date_start
		},
		{
			transform: 'translate(250, 40) rotate(6)',
			text: date_end
		},
		{
			transform: 'matrix(0.999, 0.035, -0.035, 0.999, 195.366, 55.301)',
			text: hour
		},
		{
			transform: 'translate(50.132 70.687) rotate(3)',
			text: venue
		},
		{
			transform: 'translate(80 108) rotate(6)',
			text: `in ${location}`
		}
	]
	const refs = useRefs(infoParts.length)
	const group = useRef<SVGGElement>()
	useEffect(() => {
		if (!centerIn) return
		const {width, height} = group.current.getBBox()
		const maxWidth = centerIn / scale
		const offset = (maxWidth - width) / 2
		group.current.setAttribute('transform', `translate(${offset / scale} 0)`)
	})
	return (
		<g ref={group}>
			{infoParts.map(
				(part, i) =>
					part.text && (
						<Highlight
							fill="#6AC153"
							paddingY={8}
							transform={part.transform}
							source={refs[i]}
						/>
					)
			)}
			{infoParts.map(
				(part, i) =>
					part.text && (
						<HighlightedText
							letter-spacing="-0.014em"
							fill="white"
							font-family="Campton"
							font-weight="bold"
							font-size="20.4"
							{...part}
							ref={refs[i]}
						/>
					)
			)}
		</g>
	)
}

const scale = 516 / 733

const variants = {
	default: {
		width: 516,
		height: 732,
		tagline: {
			transform: 'translate(0 35)',
			maxWidth: 516 * 0.8,
			maxHeight: 480 * 0.8,
			centerIn: 516
		},
		info: {
			transform: `translate(0 450) rotate(-2) scale(1.1)`,
			scale: 1.1,
			centerIn: 516
		},
		revolutionLogo: {
			transform: `translate(33 650) scale(.65)`
		},
		logo: {
			transform: `translate(260 628) scale(1.1)`,
			backdrop: `translate(140 530) scale(.7)`
		}
	},
	landscape: {
		width: 1640 * scale,
		height: 680 * scale,
		tagline: {
			transform: `translate(${110 * scale} ${41 * scale})`,
			maxWidth: 1280 * 0.8,
			maxHeight: 480 * 0.8,
			centerIn: 0
		},
		info: {
			transform: `translate(610 50) scale(1.25) rotate(-2)`,
			scale: 1,
			centerIn: undefined
		},
		revolutionLogo: {
			transform: `translate(40 400) scale(.65)`
		},
		logo: {
			transform: `translate(220 378) scale(1.1)`,
			backdrop: `translate(-20 ${400 * scale}) scale(.7)`
		}
	},
	square: {
		width: 1080 * scale,
		height: 1080 * scale,
		tagline: {
			transform: `translate(0 ${25 * scale})`,
			maxWidth: 1080 * scale * 0.9,
			maxHeight: 570 * scale,
			centerIn: 1080 * scale
		},
		info: {
			transform: `translate(0 470) scale(1.4) rotate(-2)`,
			scale: 1.3,
			centerIn: 1080 * scale
		},
		revolutionLogo: {
			transform: `translate(45 670) scale(0.8)`
		},
		logo: {
			transform: `translate(455 650) scale(1.3)`,
			backdrop: `translate(-30 ${715 * scale}) scale(.7)`
		}
	}
}

type Variant = typeof variants.default

type Details = {
	location: string
	venue: string
	start: Date
	end: Date
	hasEndDate: boolean
	hasEndHour: boolean
}

export type PosterProps = TagLineWords &
	Details & {variant?: keyof typeof variants}

const formatVenueDetails = ({
	location,
	venue,
	start,
	end,
	hasEndHour,
	hasEndDate
}: Details): EventDetails => {
	return {
		location,
		venue: venue || 'Waar?',
		date_start: !isValid(start)
			? 'Wanneer?'
			: format(start, 'dd MMMM yyyy', {locale: nldate}),
		date_end:
			hasEndDate && isValid(end)
				? format(end, 'dd MMMM yyyy', {locale: nldate})
				: '',
		hour: hasEndDate
			? 't.e.m.'
			: (!isValid(start) ? 'Hoe laat?' : format(start, "HH'u'mm")) +
			  (hasEndHour && isValid(end) ? format(end, " - HH'u'mm") : '')
	}
}

export const Poster = forwardRef<SVGSVGElement, PosterProps>((props, ref) => {
	const [render, setRender] = useState(0)
	const internalRef = useRef<SVGSVGElement>()
	useEffect(() => {
		internalRef &&
			internalRef.current &&
			internalRef.current!.addEventListener('load', () => {
				setRender(1)
			})
	}, [])
	const variant = variants[props.variant || 'default']
	return (
		<svg
			className={styles()}
			viewBox={`0 0 ${variant.width} ${variant.height}`}
			ref={composeRefs(ref, internalRef)}
		>
			<defs>
				<linearGradient id="bg" x1="0" x2="0" y1="0" y2="1">
					<stop offset="0%" stop-color="#C3DD26" />
					<stop offset="100%" stop-color="#AED62B" />
				</linearGradient>
			</defs>
			<g>
				<rect
					width={variant.width}
					height={variant.height}
					fill="#B0D630"
					style={{fill: 'url(#bg)'}}
				/>
			</g>
			<g transform={variant.tagline.transform}>
				<Tagline {...props} variant={variant} />
			</g>
			<g transform={variant.revolutionLogo.transform}>
				<RevolutionLogo />
			</g>
			<g transform={variant.logo.transform}>
				<Logo />
			</g>
			<g transform={variant.info.transform}>
				<Info
					scale={variant.info.scale}
					centerIn={variant.info.centerIn}
					{...formatVenueDetails(props)}
				/>
			</g>
		</svg>
	)
})
